import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Stack,
  Typography,
} from "@mui/material";

import { ErrorTypography } from "components/ErrorTypography";
import { NewTabLink } from "components/NewTabLink";
import { MultiShipmentProjectQuoteFields } from "components/QuoteFields";
import { getSubmitQuoteBlockMessage } from "utils/quotes";

import { QUOTING_MUTLI_SHIPMENT_PROJECTS_BLOG_URL } from "../../../../constants";
import { QuotesTeammates } from "../QuotesTeammates";

import type { SubmitQuoteFormShipment } from "components/QuoteFields/types";

import type { SubmitMultiShipmentProjectQuoteFormState } from "./types";

interface Props {
  onValueChanged: <T>(
    key: keyof SubmitMultiShipmentProjectQuoteFormState,
    value: T,
  ) => void;
  onSubmit: VoidFunction;
  error: string | null;
  loading: boolean;
  formState: SubmitMultiShipmentProjectQuoteFormState;
  quoteRefetchLoading: boolean;
  canSubmitQuote: boolean;
  submitBlockReason: SubmitQuoteBlockReason | null;
  formValidationError: UIError;
  onPopulateShipmentRequirements: VoidFunction;
  carrierTeammatesOpened: boolean;
  closeCarrierTeammates: VoidFunction;
  openCarrierTeammates: VoidFunction;
  shipment: SubmitQuoteFormShipment;
  secondaryAction?: React.ReactNode;
}

export function MultiShipmentSubmitQuoteForm(props: Props) {
  const {
    onValueChanged,
    onSubmit,
    error,
    loading,
    formState,
    quoteRefetchLoading,
    canSubmitQuote,
    submitBlockReason,
    formValidationError,
    onPopulateShipmentRequirements,
    carrierTeammatesOpened,
    closeCarrierTeammates,
    openCarrierTeammates,
    shipment,
    secondaryAction,
  } = props;

  if (!canSubmitQuote) {
    return (
      <Box pt="1rem">
        <Typography variant="body1">
          {submitBlockReason != null &&
            getSubmitQuoteBlockMessage(submitBlockReason)}
        </Typography>
      </Box>
    );
  }

  if (quoteRefetchLoading) {
    return (
      <Stack direction="row" justifyContent="center">
        <CircularProgress />
      </Stack>
    );
  }

  return (
    <Box pt="1rem">
      <Alert severity="info">
        {
          "This is a multi shipment project. To learn more about quoting multi shipment projects, click "
        }
        <NewTabLink href={QUOTING_MUTLI_SHIPMENT_PROJECTS_BLOG_URL}>
          <Typography>here.</Typography>
        </NewTabLink>
      </Alert>
      {shipment.teammate_quotes?.length > 0 && (
        <Alert severity="info">
          <Stack alignItems="flex-start">
            <Typography>
              Someone from your company has already quoted this load
            </Typography>
            <Button color="primary" onClick={openCarrierTeammates}>
              Click to see who has submitted quotes
            </Button>
          </Stack>
        </Alert>
      )}

      <MultiShipmentProjectQuoteFields
        shipment={shipment}
        formState={formState}
        onValueChanged={onValueChanged}
        audience="carrier"
        onPopulateShipmentRequirements={onPopulateShipmentRequirements}
        loading={loading}
        onSubmit={onSubmit}
        submitLabel="Submit Quote"
        secondaryAction={secondaryAction}
        messages={
          <Stack direction="column" spacing={1}>
            <ErrorTypography error={formValidationError} />
            <ErrorTypography error={error} />
          </Stack>
        }
      />

      <QuotesTeammates
        carrierTeammatesOpened={carrierTeammatesOpened}
        closeCarrierTeammates={closeCarrierTeammates}
        teammates={shipment.teammate_quotes}
      />
    </Box>
  );
}
